// Helpers
import { accountFormat, isNegative, isNegativeFormatted } from "@/Common/helpers/number";
import { getCellClassNameConditions, isFooterCell, isSubTotalCell } from "@/Washouts/helpers/tables";
import { and, nullishResolver, or, ternaryResolver } from "@/Common/helpers/sonar";
import { measureTextWidth } from "@/Common/helpers/measure";
// Custom components
import IconToDisplay from "@Washouts/components/tables/Common/IconToDisplay";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
// Types
import { GridRenderCellParams, GridCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";
import { DepartmentalWashoutsTableProps } from "@/Washouts/types/sales";
import { DynamicColDef } from "@/Washouts/types/commons";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";

const SMALL_CELL_CLASS = "table-small-cell";
const SUBTOTAL_NEGATIVE_CLASS = "cell-subtotal-negative";
const RIGHT_BORDERED_CELL = "right-inset";

const formatMoneyValue = (value: number): string => {
	const showValue = accountFormat(Math.abs(value));
	if (isNegative(value)) {
		return `(${showValue})`;
	}
	if (value) {
		return accountFormat(value);
	}
	return "-";
};

function getLabelColumn(labelHeaderTitle: string, labelFooterTitle: string): DynamicColDef {
	const FONT_SIZE_PX = 12;
	const DEFAULT_CELL_WIDTH = 274;
	const DEFAULT_CELL_HORIZONTAL_PADDING = 10;
	return {
		field: "label",
		headerName: labelHeaderTitle,
		width: DEFAULT_CELL_WIDTH,
		minWidth: DEFAULT_CELL_WIDTH,
		sortable: false,
		resizable: false,
		align: "left",
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} />,
		cellClassName: getCellClassNameConditions({}),
		valueFormatter: (value) => value ?? "",
		renderCell: (params) => {
			let content = params.formattedValue ?? params.value;
			const isBold = params?.row?.groupRow || params?.row?.oemRow;
			if (isFooterCell(params)) {
				content = labelFooterTitle;
			} else if (params?.value?.startsWith?.("<")) {
				content = <span title="" id="text-converted" dangerouslySetInnerHTML={{ __html: params?.value }}></span>;
			}
			const fontToUse = or(isFooterCell(params), isSubTotalCell(params.row)) ? "Montserrat" : "PTSansProNarrow";
			const weightToUse = or(isFooterCell(params), isSubTotalCell(params.row)) ? "bold" : "normal";
			const textWidth = measureTextWidth(String(content), FONT_SIZE_PX, fontToUse, ternaryResolver(isBold, "900", weightToUse));
			const shouldTooltip =
				textWidth > nullishResolver(params.colDef.width, DEFAULT_CELL_WIDTH) - 2 * DEFAULT_CELL_HORIZONTAL_PADDING;
			return (
				<TypographyCell
					params={params}
					fontSize={isBold ? "1em" : undefined}
					fontWeight={isBold ? "900" : undefined}
					tooltipProps={shouldTooltip ? { contentAsTitle: true } : undefined}
				>
					{content}
				</TypographyCell>
			);
		},
	};
}

const resolveUnitsText = (value: string, cellIsFooter = false): string => {
	if (!cellIsFooter) {
		return value;
	}
	return value + " Units";
};
function getContractsColumn(): DynamicColDef {
	return {
		field: "contracts",
		headerName: "Contracts",
		type: "number",
		aggregation: "departmentalTableSum",
		align: "right",
		width: 90,
		resizable: false,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getCellClassNameConditions({
			set: ["table-small-cell no-focus"],
			conditions: [
				{
					className: SUBTOTAL_NEGATIVE_CLASS,
					condition: (params) => {
						const negative = isNegative(params?.row?.units);
						return (negative && isSubTotalCell(params?.row)) || (isFooterCell(params) && negative);
					},
				},
			],
		}),
		renderCell: (params) => (
			<TypographyCell params={params}>
				{resolveUnitsText(params?.formattedValue ?? params?.value, isFooterCell(params))}
			</TypographyCell>
		),
		valueFormatter: (value, row) => {
			if (or(isFooterCell(row), isSubTotalCell(row))) {
				return String((row?.units ?? "0") + " Units");
			}
			if (!row?.oemRow && value) {
				return parseInt(value, 10);
			}
			if (String(value) === "0") {
				return "0";
			}
			return value ? String(value) : "";
		},
	};
}

function getUnitsColumn(tableRef?: string): DynamicColDef {
	return {
		field: "units",
		headerName: "Units",
		type: "number",
		aggregation: "departmentalTableSum",
		align: "right",
		width: 70,
		resizable: false,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getCellClassNameConditions({
			set: [SMALL_CELL_CLASS],
			conditions: [
				{
					className: SUBTOTAL_NEGATIVE_CLASS,
					condition: (params) => {
						const negative = isNegative(params?.row?.units);
						return (negative && isSubTotalCell(params?.row)) || (isFooterCell(params) && negative);
					},
				},
			],
		}),
		renderCell: (params) => <TypographyCell params={params}>{params.formattedValue}</TypographyCell>,
		valueFormatter: (value, row) => {
			if (!row?.oemRow) {
				if (isSubTotalCell(row)) {
					return tableRef === "financeGross" ? row?.units + " Units" : row?.units;
				}
				if (!row?.emptyRow) {
					if (value) {
						return parseInt(value, 10);
					}
					return "0";
				}
				return value;
			}
			return "";
		},
	};
}

const financeGrossColumnWidth = 100;
const regularGrossColumnWidth = 120;
function getGrossColumn(hasRetailAndLeaseSplit = false, isFinanceGross = false): DynamicColDef {
	return {
		field: "grossProfit",
		headerName: "Gross ($)",
		type: "number",
		aggregation: "departmentalTableSum",
		sortable: false,
		align: "right",
		flex: 0.5,
		minWidth: isFinanceGross ? financeGrossColumnWidth : regularGrossColumnWidth,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} left />,
		cellClassName: getCellClassNameFn(Boolean(hasRetailAndLeaseSplit)),
		renderCell: moneyRender,
		valueFormatter: absoluteFormatter,
	};
}

// Account Column funcs

function getAccountRendererFn(): DynamicColDef["renderCell"] {
	return (params) => {
		const value = getValueFromParams(params);
		const isNegativeValue = isNegative(value);
		if (params?.row?.emptyRow || params?.row?.oemRow) {
			return <TypographyCell params={params}>&nbsp;</TypographyCell>;
		}
		if (isFooterCell(params)) {
			let showValue;
			if (isNegativeValue) {
				showValue = `(${accountFormat(Math.abs(value))})`;
			} else {
				showValue = accountFormat(value);
			}
			return (
				<TypographyCell
					params={{ ...params, value }}
					hints={{ isNegative: isNegativeValue, isFooter: true, isPvr: params?.field === "account", isMoney: true }}
				>
					{showValue}
				</TypographyCell>
			);
		}
		if (params?.row?.subFooter || params?.row?.isFooter || params?.row?.total) {
			return (
				<TypographyCell
					params={{ ...params, value }}
					hints={{
						isNegative: isNegativeValue,
						isMoney: true,
						isPvr: params?.field === "account",
						isSubTotalRow: true,
						isFooter: isNegativeValue,
					}}
				>
					{formatMoneyValue(value)}
				</TypographyCell>
			);
		}
		return (
			<TypographyCell params={params} hints={{ isNegative: isNegativeValue }}>
				{params?.field !== "account" ? formatMoneyValue(value) : value}
			</TypographyCell>
		);
	};
}

const getAccountColumnCellClassName = (showPVR: boolean, hasRetailAndLeaseSplit: boolean) => {
	if (showPVR) {
		return getCellClassNameFn(hasRetailAndLeaseSplit);
	}
	return getCellClassNameConditions({
		set: ["!px-[8px]"],
		conditions: [
			{
				className: RIGHT_BORDERED_CELL,
				condition: (params) => and(Boolean(hasRetailAndLeaseSplit), !isFooterCell(params)),
			},
			{
				className: "!bg-[var(--primary-color)] !text-white",
				condition: (params) => isFooterCell(params),
			},
		],
		overrideDefaults: true,
	});
};
const getAccountColumnRenderCell = (showPVR: boolean) => {
	if (showPVR) {
		return getAccountRendererFn();
	}
	return (params: GridRenderCellParams) => {
		return (
			<TypographyCell
				params={params}
				hints={{
					isFooter: isFooterCell(params),
					isPvr: false,
				}}
			>
				{isFooterCell(params) ? "" : params?.formattedValue ?? params?.value}
			</TypographyCell>
		);
	};
};
const getAccountColumnValueFormatter = (value: never, row: GridValidRowModel, showPVR: boolean) => {
	if (row?.emptyRow || row?.oemRow) {
		return "";
	}
	let valueToUse: number = value;
	if (isSubTotalCell(row)) {
		valueToUse = row?.pvr;
	}
	const isFooterOrSubooter = isSubTotalCell(row) || isFooterCell(row as GridCellParams<GridValidRowModel>);
	const isNegativeValue = isNegative(valueToUse) || isNegativeFormatted(valueToUse);
	let formattedValue: string = "" + valueToUse;
	if (isFooterOrSubooter) {
		formattedValue = `${accountFormat(Math.abs(valueToUse))}`;
	}
	if (isNegativeValue) {
		formattedValue = `(${formattedValue})`;
	}
	if (isFooterOrSubooter && !showPVR) {
		return "";
	}
	return formattedValue;
};

function getAccountColumn(hasRetailAndLeaseSplit = false, useContracts = false, showPVR = false): DynamicColDef {
	return {
		field: "account",
		headerName: "Account #",
		align: "right",
		width: 138,
		resizable: false,
		sortable: false,
		headerClassName: {
			set: [HEADER_CLASS_NAME],
			conditions: [
				{
					className: RIGHT_BORDERED_CELL,
					condition: (params) => and(hasRetailAndLeaseSplit, !isFooterCell(params as GridCellParams)),
				},
			],
		},
		renderHeader: (params) => <TypographyHeader params={params} right />,
		cellClassName: getAccountColumnCellClassName(showPVR, hasRetailAndLeaseSplit),
		renderCell: getAccountColumnRenderCell(showPVR),
		valueFormatter: (value, row) => getAccountColumnValueFormatter(value, row, showPVR),
		aggregation: !useContracts ? "departmentalAccountPvr" : "departmentalAccountPvrWithContracts",
	};
}

// End Account Column funcs

function getTableColumnsMap({ tableRef, showPVR }: DepartmentalWashoutsTableProps, labelHeaderTitle: string, labelFooterTitle: string) {
	return new Map<string, DynamicColDef>([
		["id", ID_GRID_COLDEF as DynamicColDef],
		["label", getLabelColumn(labelHeaderTitle, labelFooterTitle)],
		["account", getAccountColumn(false, tableRef === "financeGross", showPVR)],
		["contracts", getContractsColumn()],
		["units", getUnitsColumn(tableRef)],
		["grossProfit", getGrossColumn(false, tableRef === "financeGross")],
		[
			"icon",
			{
				field: "icon",
				headerName: "",
				type: "string",
				align: "left",
				width: 38,
				minWidth: 38,
				maxWidth: 38,
				resizable: false,
				sortable: false,
				filterable: false,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				cellClassName: getCellClassNameConditions({
					set: ["!px-[4px]"],
				}),
				renderCell: (params) => <IconToDisplay params={params} />,
			},
		],
		[
			"emptyColumn",
			{
				field: "_id",
				headerName: "Empty",
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				width: 10,
				type: "number",
				filterable: false,
				aggregation: "sum",
			},
		],
	]);
}

const moneyRender = (params: GridRenderCellParams): JSX.Element => {
	if (isFooterCell(params)) {
		const value = params?.value;
		let formattedValue = params?.formattedValue || value;
		if (value && isNegative(value)) {
			formattedValue = `(${accountFormat(Math.abs(value))})`;
		} else if (value && value !== "-") {
			formattedValue = accountFormat(value);
		}
		return (
			<TypographyCell params={params} hints={{ isMoney: true }}>
				{formattedValue}
			</TypographyCell>
		);
	}
	return <TypographyCell params={params} />;
};

const getValueFromParams = (params: GridRenderCellParams): number => {
	let value = params?.value ?? 0;
	if (params?.field === "account" && isSubTotalCell(params.row)) {
		value = params?.row?.pvr;
	}
	return value;
};

const absoluteFormatter: DynamicColDef["valueFormatter"] = (value, row) => {
	if (!row?.emptyRow) {
		const showValue = accountFormat(Math.abs(value));
		if (isNegative(value)) {
			return `(${showValue})`;
		}
		if (value) {
			return accountFormat(value);
		}
		return "-";
	}
	return undefined;
};

function getCellClassNameFn(hasRetailAndLeaseSplit: boolean): DynamicColDef["cellClassName"] {
	return getCellClassNameConditions({
		set: ["!px-[8px]"],
		overrideDefaults: true,
		conditions: [
			{
				className: RIGHT_BORDERED_CELL,
				condition: (params) => and(Boolean(hasRetailAndLeaseSplit), !isFooterCell(params)),
			},
			{
				className: "!bg-[var(--primary-color)] !text-white",
				condition: (params) => {
					const isNegativeValue = isNegative(params?.value) || isNegativeFormatted(params?.formattedValue ?? params?.value);
					const isFooter = isFooterCell(params);
					if (isFooter && !isNegativeValue) {
						return true;
					}
					return false;
				},
			},
			{
				className: "text-[color:--text-alert-color] before:text-[color:--text-alert-color] after:text-[color:--text-alert-color]",
				condition: (params) => {
					const value = getValueFromParams(params as GridRenderCellParams);
					return +(value ?? 0) < 0;
				},
			},
			{
				className: "!bg-[color:--light-gray-color-v2]",
				condition: (params) => {
					if (!params?.value || params?.value === "-") {
						return false;
					}
					if (params?.field === "account" && isSubTotalCell(params.row)) {
						return params?.row?.pvr < 0;
					}
					return (
						(isNegative(params?.value) || isNegativeFormatted(params?.formattedValue ?? params?.value)) &&
						(isFooterCell(params) || isSubTotalCell(params.row))
					);
				},
			},
		],
	});
}

export {
	getAccountColumn,
	getGrossColumn,
	// columns helpers for washout tables
	absoluteFormatter,
	getTableColumnsMap,
	getValueFromParams,
	formatMoneyValue,
	moneyRender,
	getCellClassNameFn,
};
